// Hideo - Published videos: the logic of the page (PublishedPage.vue), the social links of the video dialog's "Released"
// tab (src/components/video/PublishedPanel.vue, draft in the video store) and "Mark as published" (setPublished; the
// Board card, the page's "Back to the plan" and the dialog's published box).
// A video is published BY HAND (user decision): video.published = true, video.publishedAt = "YYYY-MM-DDTHH:MM"; marking
// it published also moves it to "Pronto/Pubblicato" (with the actual delivery, tlMarkDone). Published videos no longer
// appear on the Board, in the Timeline and in "To do and overdue"; they stay in the Calendar (the history of the
// releases), in search and in mentions. Its release day is the publication date of the plan (publishDate) or, without
// one, the day it was marked published.
import { fmtDate, monthName, t, tn } from '../i18n'
import type { PubLinkKey, Video } from '../data/types'
import { tlFmt, tlMarkDone } from './timeline'
import { isoDay } from '../utils/date'
import { STATUS_READY } from '../data/contentTypes'
import { textMatches } from '../data/items'
import { normUrl } from '../utils/url'

/** [key (stored in video.pubLinks), display name, icon, brand colour ('' = text colour), domains (null = any)] */
type PubSocial = readonly [key: PubLinkKey, name: string, icon: string, color: string, domains: RegExp | null]
export type PubLinks = Partial<Record<PubLinkKey, string>>

// Brand names are not translated; the order is the order of the buttons and of the fields in the dialog
export const PUB_SOCIALS: readonly PubSocial[] = [
  ['youtube', 'YouTube', 'fa-brands fa-youtube', '#ff0033', /(^|\.)(youtube\.com|youtu\.be)$/],
  ['instagram', 'Instagram', 'fa-brands fa-instagram', '#e1306c', /(^|\.)instagram\.com$/],
  ['tiktok', 'TikTok', 'fa-brands fa-tiktok', '', /(^|\.)tiktok\.com$/],
  ['facebook', 'Facebook', 'fa-brands fa-facebook', '#1877f2', /(^|\.)(facebook\.com|fb\.watch|fb\.com)$/],
  ['x', 'X', 'fa-brands fa-x-twitter', '', /(^|\.)(x\.com|twitter\.com)$/],
  ['threads', 'Threads', 'fa-brands fa-threads', '', /(^|\.)threads\.(net|com)$/],
  ['linkedin', 'LinkedIn', 'fa-brands fa-linkedin', '#0a66c2', /(^|\.)linkedin\.com$/],
  ['twitch', 'Twitch', 'fa-brands fa-twitch', '#9146ff', /(^|\.)twitch\.tv$/],
  ['spotify', 'Spotify', 'fa-brands fa-spotify', '#1db954', /(^|\.)spotify\.com$/],
  ['altro', t('Other link'), 'fas fa-link', '', null]
]

/** A web address (http/https, "https://" added when missing) or '' (same rule as the legacy normUrl) */
export function pubUrl(u: unknown): string {
  const s = normUrl(String(u ?? ''))
  return /^https?:\/\/[^\s]+\.[^\s]+/i.test(s) ? s : ''
}
/** Only the valid links of the known socials (what is saved in video.pubLinks) */
export function cleanPubLinks(L: unknown): PubLinks {
  const src = (L && typeof L === 'object' ? L : {}) as Record<string, unknown>
  const o: PubLinks = {}
  for (const [k] of PUB_SOCIALS) { const u = pubUrl(src[k]); if (u) o[k] = u }
  return o
}
export const pubLinkCount = (L: unknown): number => Object.keys(cleanPubLinks(L)).length
/** The social of a link from its domain ('altro' when unknown, '' when it is not a web address) */
export function pubDetect(u: unknown): PubLinkKey | '' {
  const url = pubUrl(u)
  if (!url) return ''
  let host = ''
  try { host = new URL(url).hostname.replace(/^(www|m)\./, '') } catch { return '' }
  return (PUB_SOCIALS.find(s => s[4] && s[4].test(host)) || PUB_SOCIALS[PUB_SOCIALS.length - 1])[0]
}
/** The social buttons of a card, in the order of PUB_SOCIALS */
export function pubSocialLinks(v: Pick<Video, 'pubLinks'>): { social: PubSocial; url: string }[] {
  const L = cleanPubLinks(v.pubLinks)
  return PUB_SOCIALS.filter(s => L[s[0]]).map(social => ({ social, url: L[social[0]]! }))
}

// ===== "Released" tab of the video dialog =====
/** What the tab edits until "Save video": the links as typed (one per social), the release date and time, the notes */
export interface PubDraft {
  links: Record<string, string>
  at: string
  note: string
}
/** The tab's starting draft from the saved video (null = a new video) */
export const pubDraftOf = (v: Pick<Video, 'pubLinks' | 'publishedAt' | 'pubNote'> | null | undefined): PubDraft =>
  ({ links: { ...((v && v.pubLinks) || {}) }, at: (v && v.publishedAt) || '', note: (v && v.pubNote) || '' })
/** "Paste a link": the social it goes to (pubDetect) and the link, or null when it is not a web address */
export function pubPasted(text: string): { key: PubLinkKey; url: string } | null {
  const url = pubUrl(text), key = pubDetect(url)
  return key ? { key, url } : null
}
/** A social's field left (change): the web address it means, or the text as typed (trimmed) */
export const pubTyped = (text: string): string => pubUrl(text) || text.trim()

// ===== Mark as published =====
/** Marks a video published (on) or puts it back in the plan: the release time is kept, or now; published also moves it
 *  to "Pronto/Pubblicato" (with the actual delivery). Returns the video, or null when it does not exist. */
export function setPublished(d: { videos: Video[] }, id: string, on: boolean, now: Date = new Date()): Video | null {
  const v = d.videos.find(x => x.id === id)
  if (!v) return null
  const before = { ...v }
  v.published = on
  v.publishedAt = on ? (v.publishedAt || tlFmt(now)) : ''
  if (on && v.status !== STATUS_READY) { v.status = STATUS_READY; tlMarkDone(v, before, now) }
  return v
}

/** Release day, YYYY-MM-DD or '' */
export const pubDay = (v: Pick<Video, 'publishDate' | 'publishedAt'>): string => v.publishDate || (v.publishedAt || '').slice(0, 10)

/** Search in title, notes, SEO title and type (q already lower case and trimmed; '' = everything) */
export const matchesPub = (v: Video, q: string): boolean =>
  textMatches(q, v.title, v.notes, v.seo && v.seo.title, v.format)
/** Most recent first (day and time) */
export const sortPublished = <V extends Video>(list: V[]): V[] =>
  [...list].sort((a, b) => (pubDay(b) + (b.publishTime || '')).localeCompare(pubDay(a) + (a.publishTime || '')))

/** Videos of one release month; k = 'YYYY-MM' or 'none' (no release date) */
interface PubGroup<V extends Video = Video> {
  k: string
  vs: V[]
}
/** Groups an already sorted list by release month, keeping the order */
export function groupByMonth<V extends Video>(list: V[]): PubGroup<V>[] {
  const groups: PubGroup<V>[] = []
  for (const v of list) {
    const k = pubDay(v).slice(0, 7) || 'none'
    const g = groups.find(x => x.k === k)
    if (g) g.vs.push(v)
    else groups.push({ k, vs: [v] })
  }
  return groups
}
/** Year of a group key: 'YYYY' or 'none' */
export const yearOf = (k: string): string => (k === 'none' ? 'none' : k.slice(0, 4))
export const yearCount = (groups: PubGroup[], y: string): number =>
  groups.filter(g => yearOf(g.k) === y).reduce((s, g) => s + g.vs.length, 0)

/** Year blocks, month blocks of the chosen year and the chosen month (the most recent when the choice is not there) */
interface PubPick<V extends Video = Video> {
  years: string[]
  year: string
  months: PubGroup<V>[]
  month: string
  group: PubGroup<V>
}
export function pickYearMonth<V extends Video>(groups: PubGroup<V>[], year: string, month: string): PubPick<V> | null {
  if (!groups.length) return null
  const years = [...new Set(groups.map(g => yearOf(g.k)))]
  if (!years.includes(year)) year = years[0]
  const months = groups.filter(g => yearOf(g.k) === year)
  const group = months.find(g => g.k === month) || months[0]
  return { years, year, months, month: group.k, group }
}

/** "October 2026" (search results and the chosen month's title) */
export const monthYearLabel = (k: string): string => (k === 'none' ? t('No date') : fmtDate(new Date(k + '-01T12:00'), { month: 'long', year: 'numeric' }))
/** "October" (month blocks) */
export const monthLabel = (k: string): string => (k === 'none' ? t('No date') : monthName(+k.slice(5, 7) - 1))
/** "Released on 2 October 2026 at 18:00" (card) */
export function releasedText(v: Video): string {
  const day = pubDay(v)
  if (!day) return t('Release date not set')
  const date = fmtDate(new Date(day + 'T12:00'), { day: 'numeric', month: 'long', year: 'numeric' })
  return v.publishTime ? t('Released on {date} at {time}', { date, time: v.publishTime }) : t('Released on {date}', { date })
}
/** Page subtitle: how many published videos, and how many came out this month */
export function pubSubtitle(all: Video[], now: Date = new Date()): string {
  if (!all.length) return t('Videos that are already out end up here')
  const month = isoDay(now).slice(0, 7)
  const thisMonth = all.filter(v => pubDay(v).slice(0, 7) === month).length
  const count = tn(all.length, '{n} published video', '{n} published videos')
  return thisMonth ? t('{count} · {n} this month', { count, n: thisMonth }) : count
}
